import { prefersReducedMotion } from '@theme/utilities';

/**
 * Configuration: Visibility threshold for triggering fade-in animation
 * Value between 0 and 1 (0.1 = 10% visibility)
 * Change this value to adjust when elements start fading in
 */
const TRIGGER_THRESHOLD = 0.4;

/**
 * Animation duration in milliseconds
 */
const ANIMATION_DURATION = 600;

/**
 * CSS class name for elements that should fade in
 */
const FADE_IN_CLASS = 'scroll-fade-in';

/**
 * Attribute to track if element has been initialized
 */
const INITIALIZED_ATTR = 'data-scroll-fade-in-initialized';

/**
 * Initializes scroll fade-in animations for blocks and content within sections
 */
function initScrollFadeIn() {
  // Skip if user prefers reduced motion
  if (prefersReducedMotion()) {
    return;
  }

  // Find all blocks and content elements (not section wrappers)
  // Target: .group-block, elements with data-block-id, and direct children of section-content-wrapper
  const blockCandidates = document.querySelectorAll(
    '.group-block, [data-block-id], .section-content-wrapper > *'
  );
  
  // Filter to get unique block elements, excluding section wrappers and nested duplicates
  const elementsToAnimate = Array.from(blockCandidates).filter((el) => {
    // Skip if already initialized
    if (el.hasAttribute(INITIALIZED_ATTR)) {
      return false;
    }
    
    // Don't include section wrappers
    if (el.matches('.section-wrapper, .shopify-section, .section')) {
      return false;
    }
    
    // Don't include if it's nested inside another block we're already animating
    // (unless it's a group-block which can contain other blocks)
    if (el.matches('.group-block')) {
      // Group blocks are always included
      return true;
    }
    
    const parentBlock = el.closest('.group-block, [data-block-id]');
    if (parentBlock && parentBlock !== el && !parentBlock.matches('.group-block')) {
      return false;
    }
    
    // Skip if element is not visible (display: none, etc.)
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });

  if (elementsToAnimate.length === 0) {
    return;
  }

  // Create Intersection Observer for scroll detection
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.target instanceof HTMLElement) {
          // Fade in when element enters viewport
          entry.target.style.opacity = '1';
          observer.unobserve(entry.target);
        }
      });
    },
    {
      threshold: TRIGGER_THRESHOLD,
      rootMargin: '0px',
    }
  );

  // Create a separate observer to check initial visibility (any part visible)
  const initialVisibilityObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.target instanceof HTMLElement) {
          const element = entry.target;
          element.setAttribute(INITIALIZED_ATTR, '');
          element.classList.add(FADE_IN_CLASS);
          element.style.transition = `opacity ${ANIMATION_DURATION}ms ease-out`;

          if (entry.isIntersecting) {
            // Already visible - keep it visible (opacity 1)
            element.style.opacity = '1';
          } else {
            // Not in viewport - set to 0 and observe for scroll
            element.style.opacity = '0';
            observer.observe(element);
          }
          
          // Unobserve from initial check
          initialVisibilityObserver.unobserve(element);
        }
      });
    },
    {
      threshold: 0, // Any visibility counts
      rootMargin: '0px',
    }
  );

  // Check initial visibility using Intersection Observer (more reliable)
  elementsToAnimate.forEach((element) => {
    if (!(element instanceof HTMLElement)) return;
    initialVisibilityObserver.observe(element);
  });

  // Handle dynamically added content (Section Rendering API)
  const mutationObserver = new MutationObserver((mutations) => {
    const newElements = /** @type {HTMLElement[]} */ ([]);

    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node instanceof HTMLElement) {
          // Check for blocks and content within the added node (not section wrappers)
          const blockCandidates = node.matches && node.matches('.group-block, [data-block-id]')
            ? [node]
            : node.querySelectorAll
            ? node.querySelectorAll('.group-block, [data-block-id], .section-content-wrapper > *')
            : [];
          
          blockCandidates.forEach((block) => {
            if (!(block instanceof HTMLElement)) return;
            
            // Don't include section wrappers
            if (block.matches('.section-wrapper, .shopify-section, .section')) {
              return;
            }
            
            // Don't include if already initialized
            if (block.hasAttribute(INITIALIZED_ATTR)) {
              return;
            }
            
            // Don't include if it's nested inside another block we're already animating
            // (unless it's a group-block)
            if (block.matches('.group-block')) {
              newElements.push(block);
              return;
            }
            
            const parentBlock = block.closest('.group-block, [data-block-id]');
            if (parentBlock && parentBlock !== block && !parentBlock.matches('.group-block')) {
              return;
            }
            
            newElements.push(block);
          });
        }
      });
    });

    // Initialize new elements using the same initial visibility observer
    if (newElements.length > 0) {
      newElements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          initialVisibilityObserver.observe(element);
        }
      });
    }
  });

  // Observe the main content area for new sections/blocks
  const mainContent = document.querySelector('#MainContent');
  if (mainContent) {
    mutationObserver.observe(mainContent, {
      childList: true,
      subtree: true,
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initScrollFadeIn);
} else {
  // DOM is already ready
  initScrollFadeIn();
}

